import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import interviewService from "../../services/interviewService";
import applicationService from "../../services/applicationService";
import { getErrorMessage } from "../../utils/errorHandler";
import { Calendar, Clock, MapPin, User, Mail, Phone, FileText, Save, X, AlertCircle, CheckCircle } from "lucide-react";
import "../../styles/recruitment.css";

const InterviewScheduling = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { applicationId } = useParams();
  
  const [application, setApplication] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [jobOffer, setJobOffer] = useState(null);
  const [existingInterview, setExistingInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [conflict, setConflict] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    application_id: parseInt(applicationId),
    date: "",
    start_time: "",
    end_time: "",
    mode: "PRESENTIAL",
    location: "",
    interviewer: "",
    comment: "",
  });

  const modeOptions = [
    { value: "PRESENTIAL", label: "Présentiel" },
    { value: "VIDEO", label: "Visioconférence" },
    { value: "PHONE", label: "Téléphone" },
  ];

  useEffect(() => {
    loadData();
  }, [applicationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load application details
      const appData = await applicationService.getById(applicationId);
      setApplication(appData);
      setCandidate(appData.candidate);
      setJobOffer(appData.job_offer);

      // Check for existing interview
      try {
        const response = await interviewService.getByApplication(applicationId);
        // API returns {exists: bool, interview: obj|null}
        if (response && response.exists && response.interview) {
          setExistingInterview(response.interview);
          setIsEditing(true);
          
          // Pre-fill form with existing data
          setFormData({
            application_id: parseInt(applicationId),
            date: response.interview.date,
            start_time: response.interview.start_time,
            end_time: response.interview.end_time,
            mode: response.interview.mode,
            location: response.interview.location || "",
            interviewer: response.interview.interviewer || "",
            comment: response.interview.comment || "",
          });
        } else {
          // No existing interview - that's fine
          setExistingInterview(null);
          setIsEditing(false);
        }
      } catch (err) {
        // Handle actual network errors
        console.error("Error loading interview:", err);
        setExistingInterview(null);
        setIsEditing(false);
      }

      setError("");
    } catch (err) {
      console.error("Error loading data:", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setConflict(null); // Clear conflict on change
  };

  const checkForConflict = async () => {
    if (!formData.date || !formData.start_time || !formData.end_time) {
      return;
    }

    try {
      const conflictData = await interviewService.checkConflict(
        formData.date,
        formData.start_time,
        formData.end_time,
        existingInterview?.id
      );

      if (conflictData.conflict) {
        setConflict(conflictData.message);
        return false;
      } else {
        setConflict(null);
        return true;
      }
    } catch (err) {
      console.error("Error checking conflict:", err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setConflict(null);

    // Validate required fields
    if (!formData.date || !formData.start_time || !formData.end_time || !formData.mode) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    // Validate time
    if (formData.start_time >= formData.end_time) {
      setError("L'heure de fin doit èªtre après l'heure de début");
      return;
    }

    // Check for conflicts
    const noConflict = await checkForConflict();
    if (!noConflict) {
      if (!conflict) {
        setError("Erreur lors de la vérification des conflits");
      }
      return;
    }

    try {
      if (isEditing && existingInterview) {
        await interviewService.update(existingInterview.id, formData);
        setSuccess("Entretien modifié avec succès");
      } else {
        await interviewService.create(formData);
        setSuccess("Entretien planifié avec succès");
      }

      setTimeout(() => {
        navigate("/rh/applications");
      }, 2000);
    } catch (err) {
      console.error("Error saving interview:", err);
      setError(getErrorMessage(err));
    }
  };

  const handleCancel = async () => {
    if (!existingInterview) {
      navigate("/rh/applications");
      return;
    }

    if (window.confirm("èŠtes-vous sè»r de vouloir annuler cet entretien ? Un email sera envoyé au candidat.")) {
      try {
        await interviewService.cancel(existingInterview.id);
        setSuccess("Entretien annulé avec succès");
        setTimeout(() => {
          navigate("/rh/applications");
        }, 2000);
      } catch (err) {
        console.error("Error cancelling interview:", err);
        setError(getErrorMessage(err));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString;
  };

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div className="page-header">
        <h1>{isEditing ? "Modifier l'entretien" : "Planifier un entretien"}</h1>
        <p className="page-subtitle">
          {isEditing ? "Modifier les détails de l'entretien" : "Planifier un entretien pour le candidat"}
        </p>
      </div>

      {error && (
        <div className="alert alert-danger">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {conflict && (
        <div className="alert alert-warning">
          <AlertCircle size={16} />
          {conflict}
        </div>
      )}

      {/* Candidate Information Card */}
      <div className="card-inner-box">
        <div className="card-box-header">
          <h3>Informations du candidat</h3>
        </div>
        <div className="candidate-info-grid">
          <div className="info-item">
            <User size={18} className="info-icon" />
            <div>
              <label>Nom</label>
              <p>{candidate ? `${candidate.first_name} ${candidate.last_name}` : "-"}</p>
            </div>
          </div>
          <div className="info-item">
            <Mail size={18} className="info-icon" />
            <div>
              <label>Email</label>
              <p>{candidate?.email || "-"}</p>
            </div>
          </div>
          <div className="info-item">
            <Phone size={18} className="info-icon" />
            <div>
              <label>Téléphone</label>
              <p>{candidate?.phone || "-"}</p>
            </div>
          </div>
          <div className="info-item">
            <FileText size={18} className="info-icon" />
            <div>
              <label>Offre d'emploi</label>
              <p>{jobOffer?.title || "-"}</p>
            </div>
          </div>
          {candidate?.cv_path && (
            <div className="info-item">
              <FileText size={18} className="info-icon" />
              <div>
                <label>CV</label>
                <a href={candidate.cv_path} target="_blank" rel="noopener noreferrer" className="cv-link">
                  Voir le CV
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Interview Scheduling Form */}
      <div className="card-inner-box" style={{ marginTop: "20px" }}>
        <div className="card-box-header">
          <h3>Détails de l'entretien</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="interview-form">
          <div className="form-row">
            <div className="form-group">
              <label>Date de l'entretien *</label>
              <div className="input-with-icon">
                <Calendar size={18} />
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Heure de début *</label>
              <div className="input-with-icon">
                <Clock size={18} />
                <input
                  type="time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Heure de fin *</label>
              <div className="input-with-icon">
                <Clock size={18} />
                <input
                  type="time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mode d'entretien *</label>
              <select
                name="mode"
                value={formData.mode}
                onChange={handleFormChange}
                required
              >
                {modeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lieu ou lien de réunion</label>
              <div className="input-with-icon">
                <MapPin size={18} />
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  placeholder={
                    formData.mode === "VIDEO" 
                      ? "Lien Zoom/Teams/Meet" 
                      : formData.mode === "PHONE"
                      ? "Numéro de téléphone"
                      : "Adresse physique"
                  }
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Interviewer</label>
              <div className="input-with-icon">
                <User size={18} />
                <input
                  type="text"
                  name="interviewer"
                  value={formData.interviewer}
                  onChange={handleFormChange}
                  placeholder="Nom de l'interviewer"
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Commentaire interne RH</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleFormChange}
                placeholder="Notes internes pour l'équipe RH..."
                rows={4}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              <Save size={18} />
              {isEditing ? "Modifier l'entretien" : "Planifier l'entretien"}
            </button>
            
            {isEditing && existingInterview && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleCancel}
              >
                <X size={18} />
                Annuler l'entretien
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/rh/applications")}
            >
              Retour
            </button>
          </div>
        </form>
      </div>

      {/* Existing Interview Info (if editing) */}
      {isEditing && existingInterview && (
        <div className="card-inner-box" style={{ marginTop: "20px" }}>
          <div className="card-box-header">
            <h3>Informations actuelles</h3>
          </div>
          <div className="interview-info-grid">
            <div className="info-item">
              <label>Date</label>
              <p>{formatDate(existingInterview.date)}</p>
            </div>
            <div className="info-item">
              <label>Heure</label>
              <p>{formatTime(existingInterview.start_time)} - {formatTime(existingInterview.end_time)}</p>
            </div>
            <div className="info-item">
              <label>Mode</label>
              <p>{existingInterview.mode === "PRESENTIAL" ? "Présentiel" : 
                 existingInterview.mode === "VIDEO" ? "Visioconférence" : "Téléphone"}</p>
            </div>
            <div className="info-item">
              <label>Lieu</label>
              <p>{existingInterview.location || "Non spécifié"}</p>
            </div>
            <div className="info-item">
              <label>Interviewer</label>
              <p>{existingInterview.interviewer || "Non spécifié"}</p>
            </div>
            <div className="info-item">
              <label>Statut</label>
              <p>{existingInterview.status === "SCHEDULED" ? "Planifié" : 
                 existingInterview.status === "COMPLETED" ? "Terminé" : "Annulé"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewScheduling;

